/**
 * Scheduling Helper Utilities
 * 
 * Provides timezone detection, schedule validation, conflict detection,
 * and formatting utilities for post scheduling functionality.
 */

import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import relativeTime from 'dayjs/plugin/relativeTime';

// Extend dayjs with required plugins
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);

/**
 * Conflict window configurations in minutes
 */
export const CONFLICT_WINDOWS = {
    TIGHT: 3,      // 3 minutes - for real-time platforms
    DEFAULT: 5,    // 5 minutes - standard window
    RELAXED: 10,   // 10 minutes - for less time-sensitive content
    CUSTOM: null   // User-defined
};

/**
 * Detects the user's browser timezone
 * @returns {string} Timezone identifier (e.g., 'America/New_York')
 */
export const detectBrowserTimezone = () => {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    } catch (error) {
        console.error('Error detecting timezone:', error);
        return 'UTC';
    }
};

/**
 * Gets the user's timezone from their profile or browser
 * @param {Object} userProfile - User profile object with timezone field
 * @returns {string} Timezone identifier
 */
export const getUserTimezone = (userProfile) => {
    return userProfile?.timezone || detectBrowserTimezone();
};

/**
 * Gets timezone display information
 * @param {Object} userProfile - User profile object
 * @returns {Object} Timezone display info
 */
export const getTimezoneDisplayInfo = (userProfile) => {
    const browserTz = detectBrowserTimezone();
    const profileTz = userProfile?.timezone || browserTz;
    const now = dayjs();
    
    // Get timezone offsets to ensure we have valid data
    const browserOffset = now.tz(browserTz).format('Z');
    const profileOffset = now.tz(profileTz).format('Z');
    
    // Try to get timezone abbreviations, fallback to offset if not available
    let browserAbbr, profileAbbr;
    
    try {
        // Try to get abbreviation using Intl.DateTimeFormat
        browserAbbr = new Intl.DateTimeFormat('en', {
            timeZone: browserTz,
            timeZoneName: 'short'
        }).formatToParts(new Date()).find(part => part.type === 'timeZoneName')?.value || browserOffset;
        
        profileAbbr = new Intl.DateTimeFormat('en', {
            timeZone: profileTz,
            timeZoneName: 'short'
        }).formatToParts(new Date()).find(part => part.type === 'timeZoneName')?.value || profileOffset;
    } catch (error) {
        // Fallback to offset if abbreviations fail
        console.warn('Error getting timezone abbreviations, using offsets:', error);
        browserAbbr = browserOffset;
        profileAbbr = profileOffset;
    }
    
    // Check if they're different
    const isDifferent = browserTz !== profileTz;
    
    // Create more user-friendly display text
    let displayText;
    if (isDifferent) {
        displayText = `${profileAbbr} (Profile)`;
    } else {
        displayText = profileAbbr;
    }
    
    console.log('Timezone info generated:', {
        browserTz,
        profileTz,
        browserAbbr,
        profileAbbr,
        isDifferent,
        displayText
    });
    
    return {
        browserTimezone: browserTz,
        profileTimezone: profileTz,
        browserAbbr,
        profileAbbr,
        isDifferent,
        displayText
    };
};

/**
 * Validates a schedule date based on business rules
 * @param {string} scheduleDate - ISO format date string
 * @param {string} subscriptionTier - User's subscription tier
 * @returns {Object} Validation result with isValid and message
 */
export const validateScheduleDate = (scheduleDate, subscriptionTier = 'free') => {
    if (!scheduleDate) {
        return { isValid: false, message: 'Schedule date is required' };
    }
    
    const scheduledTime = dayjs(scheduleDate);
    const now = dayjs();
    
    // Check if date is in the past
    if (scheduledTime.isBefore(now)) {
        return { isValid: false, message: 'Schedule date cannot be in the past' };
    }
    
    // Check minimum time ahead (3 minutes)
    const minutesAhead = scheduledTime.diff(now, 'minutes');
    if (minutesAhead < 3) {
        return { 
            isValid: false, 
            message: 'Posts must be scheduled at least 3 minutes in advance' 
        };
    }
    
    // Check maximum scheduling limits based on subscription
    const maxScheduleDays = {
        'free': 90,          // 3 months
        'basic': 90,         // 3 months  
        'premium': 365,      // 1 year
        'enterprise': 365,   // 1 year
        'Elite Advantage': 730,  // 24 months
        'Internal Account': 730  // 24 months
    };
    
    const maxDays = maxScheduleDays[subscriptionTier] || 90;
    const daysAhead = scheduledTime.diff(now, 'days');
    
    if (daysAhead > maxDays) {
        return { 
            isValid: false, 
            message: `Your plan allows scheduling up to ${maxDays} days in advance` 
        };
    }
    
    return { isValid: true, message: '' };
};

/**
 * Formats a scheduled date for display
 * @param {string} scheduleDate - ISO format date string
 * @param {string} timezone - Timezone to display in
 * @returns {string} Formatted date string
 */
export const formatScheduledDate = (scheduleDate, timezone = 'UTC') => {
    if (!scheduleDate) return '';
    
    return dayjs(scheduleDate)
        .tz(timezone)
        .format('MMM D, YYYY [at] h:mm A');
};

/**
 * Formats a scheduled time for display with relative time
 * @param {string} scheduleDate - ISO format date string
 * @param {string} timezone - Timezone to display in
 * @returns {string} Formatted time string with relative component
 */
export const formatScheduledTime = (scheduleDate, timezone = 'UTC') => {
    if (!scheduleDate) return '';
    
    const scheduled = dayjs(scheduleDate).tz(timezone);
    const now = dayjs().tz(timezone);
    const relative = scheduled.fromNow();
    
    // If it's today or tomorrow, show relative time
    if (scheduled.diff(now, 'days') < 2) {
        return `${scheduled.format('h:mm A')} (${relative})`;
    }
    
    return formatScheduledDate(scheduleDate, timezone);
};

/**
 * Check for scheduling conflicts
 * @param {string} scheduledTime - Proposed schedule time (ISO format)
 * @param {Array} existingPosts - Array of existing scheduled posts
 * @param {number} conflictWindow - Conflict window in minutes
 * @param {string} excludePostId - Post ID to exclude (for edits)
 * @returns {Object} Conflict detection result
 */
export const detectSchedulingConflicts = (
    scheduledTime,
    existingPosts = [],
    conflictWindow = CONFLICT_WINDOWS.DEFAULT,
    excludePostId = null
) => {
    const proposedTime = dayjs(scheduledTime);
    const conflicts = [];
    const duplicateContent = [];
    
    existingPosts.forEach(post => {
        // Skip if this is the post being edited
        if (excludePostId && post.id === excludePostId) return;
        
        // Skip non-scheduled posts
        if (!post.scheduled_post_date) return;
        
        const postTime = dayjs(post.scheduled_post_date);
        const timeDiff = Math.abs(postTime.diff(proposedTime, 'minutes'));
        
        // Check for time conflicts
        if (timeDiff <= conflictWindow) {
            conflicts.push({
                ...post,
                timeDifference: timeDiff,
                conflictType: 'timing'
            });
        }
        
        // Check for duplicate content within 24 hours
        const hoursDiff = Math.abs(postTime.diff(proposedTime, 'hours'));
        if (hoursDiff <= 24 && post.post_text) {
            // This would be enhanced to check actual content similarity
            duplicateContent.push({
                ...post,
                hoursDifference: hoursDiff,
                conflictType: 'duplicate'
            });
        }
    });
    
    return {
        hasConflicts: conflicts.length > 0,
        hasDuplicates: duplicateContent.length > 0,
        conflicts,
        duplicateContent,
        totalConflicts: conflicts.length + duplicateContent.length
    };
};

/**
 * Call API to check for conflicts
 * @param {Object} params - Parameters for conflict check
 * @returns {Promise<Object>} API response with conflicts and suggestions
 */
export const checkConflictsAPI = async ({
    scheduledTime,
    conflictWindow = CONFLICT_WINDOWS.DEFAULT,
    excludePostId = null,
    platforms = [],
    postText = '',
    token
}) => {
    try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}post/posts/check-conflicts/`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Token ${token}`
            },
            body: JSON.stringify({
                scheduled_time: scheduledTime,
                conflict_window: conflictWindow,
                exclude_post_id: excludePostId,
                platforms: platforms,
                post_text: postText,
                check_duplicate_content: true
            })
        });
        
        if (!response.ok) {
            throw new Error('Failed to check conflicts');
        }
        
        return await response.json();
    } catch (error) {
        console.error('Error checking conflicts:', error);
        throw error;
    }
};

/**
 * Generate suggested times based on conflicts
 * @param {string} originalTime - Originally selected time
 * @param {Array} conflicts - Array of conflicting posts
 * @param {Array} platforms - Selected platforms
 * @param {number} conflictWindow - Conflict window in minutes
 * @returns {Array} Array of suggested times
 */
export const getSuggestedTimes = (
    originalTime,
    conflicts = [],
    platforms = [],
    conflictWindow = CONFLICT_WINDOWS.DEFAULT
) => {
    const suggestions = [];
    const baseTime = dayjs(originalTime);
    
    // 1. Next available slot
    const nextSlot = findNextOpenSlot(baseTime, conflicts, conflictWindow);
    if (nextSlot) {
        suggestions.push({
            time: nextSlot.toISOString(),
            displayTime: formatScheduledTime(nextSlot.toISOString()),
            reason: 'Next available slot',
            type: 'next_available'
        });
    }
    
    // 2. Platform peak times
    const peakTimes = getPlatformPeakTimes(baseTime, platforms);
    peakTimes.forEach(({ time, platform }) => {
        const peakTimeDayjs = dayjs(time);
        if (!hasConflictAtTime(peakTimeDayjs, conflicts, conflictWindow)) {
            suggestions.push({
                time: time,
                displayTime: formatScheduledTime(time),
                reason: `Peak hours for ${platform}`,
                type: 'peak_hours'
            });
        }
    });
    
    // 3. Smart offset (30 minutes later if no conflicts)
    const smartOffset = baseTime.add(30, 'minutes');
    if (!hasConflictAtTime(smartOffset, conflicts, conflictWindow)) {
        suggestions.push({
            time: smartOffset.toISOString(),
            displayTime: formatScheduledTime(smartOffset.toISOString()),
            reason: '30 minutes later',
            type: 'offset'
        });
    }
    
    // Remove duplicates and limit to 3
    const uniqueSuggestions = suggestions.filter((suggestion, index, self) =>
        index === self.findIndex(s => s.time === suggestion.time)
    );
    
    return uniqueSuggestions.slice(0, 3);
};

/**
 * Find next available time slot
 * @param {dayjs} startTime - Starting time to search from
 * @param {Array} conflicts - Existing scheduled posts
 * @param {number} conflictWindow - Conflict window in minutes
 * @returns {dayjs|null} Next available time or null
 */
export const findNextOpenSlot = (startTime, conflicts, conflictWindow) => {
    let checkTime = startTime.add(conflictWindow + 1, 'minutes');
    const maxCheckTime = startTime.add(24, 'hours');
    
    while (checkTime.isBefore(maxCheckTime)) {
        if (!hasConflictAtTime(checkTime, conflicts, conflictWindow)) {
            // Round to nearest 15 minutes for cleaner times
            const minutes = checkTime.minute();
            const roundedMinutes = Math.ceil(minutes / 15) * 15;
            return checkTime.minute(roundedMinutes).second(0);
        }
        checkTime = checkTime.add(15, 'minutes');
    }
    
    return null;
};

/**
 * Check if a specific time has conflicts
 * @param {dayjs} checkTime - Time to check
 * @param {Array} conflicts - Existing scheduled posts
 * @param {number} conflictWindow - Conflict window in minutes
 * @returns {boolean} True if conflicts exist
 */
const hasConflictAtTime = (checkTime, conflicts, conflictWindow) => {
    return conflicts.some(post => {
        if (!post.scheduled_post_date) return false;
        const postTime = dayjs(post.scheduled_post_date);
        const timeDiff = Math.abs(postTime.diff(checkTime, 'minutes'));
        return timeDiff <= conflictWindow;
    });
};

/**
 * Get platform-specific peak posting times
 * @param {dayjs} baseTime - Base time to find peaks around
 * @param {Array} platforms - Selected platforms
 * @returns {Array} Array of peak times with platform info
 */
const getPlatformPeakTimes = (baseTime, platforms) => {
    const peakHours = {
        twitter: [9, 12, 17, 20],
        facebook: [9, 13, 15, 19],
        instagram: [6, 12, 17, 19],
        linkedin: [7, 10, 12, 17],
        tiktok: [6, 10, 19, 22],
        bluesky: [9, 12, 17, 20], // Similar to Twitter
        mastodon: [9, 17, 20],     // Similar to Twitter
        threads: [7, 12, 18]       // Similar to Instagram
    };
    
    const suggestions = [];
    
    platforms.forEach(platform => {
        const platformLower = platform.toLowerCase();
        const peaks = peakHours[platformLower] || [9, 12, 17];
        
        // Find next peak hour
        const currentHour = baseTime.hour();
        let nextPeakHour = peaks.find(hour => hour > currentHour);
        
        if (nextPeakHour) {
            const peakTime = baseTime.hour(nextPeakHour).minute(0).second(0);
            suggestions.push({
                time: peakTime.toISOString(),
                platform: platform
            });
        } else {
            // Next day's first peak
            const nextDay = baseTime.add(1, 'day');
            const peakTime = nextDay.hour(peaks[0]).minute(0).second(0);
            suggestions.push({
                time: peakTime.toISOString(),
                platform: platform
            });
        }
    });
    
    return suggestions;
};

/**
 * Format conflict information for display
 * @param {Object} conflict - Conflict object
 * @returns {Object} Formatted conflict for UI display
 */
export const formatConflictForDisplay = (conflict) => {
    const timeDiff = conflict.timeDifference || 0;
    const platforms = conflict.platforms || [];
    
    return {
        id: conflict.id,
        summary: conflict.post_text?.substring(0, 100) + (conflict.post_text?.length > 100 ? '...' : ''),
        scheduledTime: formatScheduledTime(conflict.scheduled_post_date),
        platforms: platforms.join(', '),
        severity: timeDiff <= 3 ? 'high' : timeDiff <= 5 ? 'medium' : 'low',
        message: `Scheduled ${timeDiff} minutes ${timeDiff < 0 ? 'before' : 'after'} your selected time`
    };
}; 